// components/Table/types.ts
export type FieldKind =
  | "integer"
  | "decimal"
  | "date"
  | "text"
  | "boolean"
  | "enum"
  | "array"
  | "linked_record"
  | "iso_week";
export type NumericOp = "eq" | "neq" | "gt" | "gte" | "lt" | "lte";
export type TextOp = "eq" | "neq" | "like";
export type BoolOp = "eq" | "neq";
export type EnumOp = "eq" | "neq" | "in" | "nin"; // eq/neq = single select, in/nin = multi-select
export type IsoWeekOp = "eq" | "neq" | "gt" | "gte" | "lt" | "lte";
export type ArrayOp = "in" | "nin" | "is_empty" | "is_not_empty"; // in/nin = multi-select like enum
export type LinkedRecordOp = "like" | "eq" | "neq";
export type Operator = NumericOp | TextOp | BoolOp | EnumOp | ArrayOp | LinkedRecordOp | IsoWeekOp;

// Match mode for filter logic: "all" = AND (every), "any" = OR (some)
export type MatchMode = "all" | "any";

export type OpsByKind = {
  integer: NumericOp;
  decimal: NumericOp;
  date: NumericOp;
  text: TextOp;
  boolean: BoolOp;
  enum: EnumOp;
  array: ArrayOp;
  linked_record: LinkedRecordOp;
  iso_week: IsoWeekOp;
};
export type OpFor<K extends FieldKind> = OpsByKind[K];

export type MenuType = "filter" | "visible" | "orderby" | "groupby";

export type EnumOption = { value: string; label?: string; color?: string };

export type LinkedRecordConfig = {
  labelKey?: string; // default "name"
  valueKey?: string; // default "id"
  truncate?: number; // default 6
};

export type FieldSpec<FN extends string> = {
  name: FN;
  kind: FieldKind;
  allowed?: readonly OpFor<FieldKind>[];
  menus?: MenuType[];
  displayName?: string | ((name?: string) => string);
  options?: EnumOption[]; // For enum fields: list of allowed values with optional colors
  linkedRecord?: LinkedRecordConfig; // For linked_record fields: subfield configuration
};

// Helper to format field name: replace underscores with spaces and title case
export const displayName = (name: string): string => {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// Helper to resolve displayName
export const getDisplayName = <FN extends string>(field: FieldSpec<FN>): string => {
  if (!field.displayName) return displayName(field.name);
  if (typeof field.displayName === "function") return field.displayName(field.name);
  return field.displayName;
};

// Expression for a single filter condition
export type Expression = {
  operator: Operator;
  value: unknown;
  subfield?: string; // For linked_record: which part to filter on (e.g., "name" or "id")
};

// Field filter with multiple expressions
export type FieldFilter<FN extends string> = {
  name: FN;
  kind: FieldKind;
  expressions: Expression[];
  matchMode?: MatchMode; // "all" = AND (default), "any" = OR
};

export type Order<FN extends string> = { name: FN; direction: "ascending" | "descending" };
export type Visible<FN extends string> = { name: FN; visible: boolean };

export type Query<FN extends string> = {
  id?: string;
  name?: string;
  filters: FieldFilter<FN>[];
  filterMatchMode?: MatchMode; // "all" = AND (default), "any" = OR for combining field filters
  visible?: Visible<FN>[];
  hidden?: Visible<FN>[];
  orderby?: Order<FN>;
  groupby?: FN[];
};

// factory returns defaultQuery shape
export const makeDefaultQuery = <FN extends string>(): Query<FN> => ({
  name: "Default",
  filters: [],
  filterMatchMode: "all",
  visible: [],
});

// Helper to check if query has any expressions (determines "dirty" state)
export const hasExpressions = <FN extends string>(query: Query<FN>): boolean => {
  return query.filters?.some((f) => f.expressions?.length > 0) ?? false;
};

// Format a linked_record value as "VALUE - Label"
export const formatLinkedRecord = (value: any, config?: LinkedRecordConfig): string => {
  if (!value || typeof value !== "object") return "";
  const labelKey = config?.labelKey || "name";
  const valueKey = config?.valueKey || "id";
  const truncate = config?.truncate ?? 6;
  const label = value[labelKey] || "";
  const id = value[valueKey] || "";
  const shortId = truncate > 0 ? id.toString().slice(0, truncate).toUpperCase() : "";
  if (!label && !shortId) return "";
  if (!shortId) return label;
  if (!label) return shortId;
  return `${shortId} - ${label}`;
};

// Defaults for menus
export const DEFAULT_OPS: { [K in FieldKind]: readonly OpFor<K>[] } = {
  integer: ["eq", "neq", "gt", "gte", "lt", "lte"],
  decimal: ["eq", "neq", "gt", "gte", "lt", "lte"],
  date: ["eq", "neq", "gt", "gte", "lt", "lte"],
  text: ["like", "eq", "neq"],
  boolean: ["eq", "neq"],
  enum: ["eq", "neq", "in", "nin"],
  array: ["in", "nin", "is_empty", "is_not_empty"],
  linked_record: ["like", "eq", "neq"],
  iso_week: ["eq", "neq", "gt", "gte", "lt", "lte"],
} as const;
